import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, FileText, Download, Layers, Image as ImageIcon } from 'lucide-react';

/**
 * Wave 3.55 — ImageListPdfDialog
 *
 * Opens a modal that lets the user choose:
 *  1. Whether to print the company logo + name on the page header
 *  2. Whether to group cards by classification (banners) or stream
 *     them continuously
 *
 * After "Generate", the dialog kicks off a background job on the
 * backend (POST /reports/image-list/jobs), then polls the status
 * endpoint until ``ready``.  A progress bar shows ``done / total``
 * packets so the user knows the report is being assembled — this is
 * the safety net for catalogues with hundreds of images that would
 * otherwise risk a 60 s proxy timeout if the PDF were streamed
 * synchronously.
 *
 * Props:
 *   open / onOpenChange   — standard dialog controls
 *   companyId             — required (super-admin) or auto-derived
 *   scope                 — 'diamond' | 'color_stone' | 'pearl' | 'unified'
 *   moduleLabel           — display label ('Diamonds', 'Colour Stones', …)
 *   defaultFilename       — fallback file name if the backend omits one
 */
const PREF_KEY_BRANDING = 'pdf.include_branding';
const PREF_KEY_GROUPED = 'imageList.grouped';

export function ImageListPdfDialog({
  open,
  onOpenChange,
  companyId,
  scope,
  moduleLabel,
  defaultFilename,
}) {
  const initialBranding = (() => {
    try {
      const v = localStorage.getItem(PREF_KEY_BRANDING);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch { /* noop */ }
    return true;
  })();
  const initialGrouped = (() => {
    try {
      const v = localStorage.getItem(PREF_KEY_GROUPED);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch { /* noop */ }
    return true;
  })();

  const [includeBranding, setIncludeBranding] = useState(initialBranding);
  const [grouped, setGrouped] = useState(initialGrouped);
  const [company, setCompany] = useState(null);

  // Job state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // queued|running|ready|failed
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  // Load company branding details (used to render a tiny preview card).
  useEffect(() => {
    if (!open || !companyId) { setCompany(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/companies/${companyId}/logo`);
        if (!cancelled) setCompany(data);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  // Reset job state when dialog closes.
  useEffect(() => {
    if (!open) {
      setJobId(null);
      setJobStatus(null);
      setProgress({ done: 0, total: 0 });
      setDownloading(false);
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    }
  }, [open]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // Persist user prefs so the next open remembers their choices.
  useEffect(() => {
    try { localStorage.setItem(PREF_KEY_BRANDING, includeBranding ? '1' : '0'); } catch { /* noop */ }
  }, [includeBranding]);
  useEffect(() => {
    try { localStorage.setItem(PREF_KEY_GROUPED, grouped ? '1' : '0'); } catch { /* noop */ }
  }, [grouped]);

  const pollJob = async (id) => {
    try {
      const { data } = await api.get(`/reports/image-list/jobs/${id}`);
      setJobStatus(data.status);
      setProgress(data.progress || { done: 0, total: 0 });
      if (data.status === 'ready') {
        await downloadJob(id, data.filename || defaultFilename || `image_list_${scope}.pdf`);
        return;
      }
      if (data.status === 'failed') {
        toast.error(`Image List PDF failed: ${data.error || 'Unknown error'}`);
        return;
      }
      // queued / running — poll again shortly
      pollRef.current = setTimeout(() => pollJob(id), 800);
    } catch (e) {
      toast.error(extractError(e, 'Polling failed'));
      setJobStatus('failed');
    }
  };

  const downloadJob = async (id, filename) => {
    setDownloading(true);
    try {
      const res = await api.get(`/reports/image-list/jobs/${id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('Image List PDF downloaded');
      onOpenChange(false);
    } catch (e) {
      toast.error(extractError(e, 'Download failed'));
    } finally {
      setDownloading(false);
    }
  };

  const startJob = async () => {
    if (!scope) { toast.error('Missing scope'); return; }
    if (!companyId) { toast.error('Pick a company first'); return; }
    setJobStatus('queued');
    setProgress({ done: 0, total: 0 });
    try {
      const { data } = await api.post('/reports/image-list/jobs', {
        company_id: companyId,
        scope,
        grouped,
        include_branding: includeBranding,
      });
      setJobId(data.job_id);
      pollRef.current = setTimeout(() => pollJob(data.job_id), 400);
    } catch (e) {
      toast.error(extractError(e, 'Could not start PDF job'));
      setJobStatus('failed');
    }
  };

  const isBusy = jobStatus === 'queued' || jobStatus === 'running' || downloading;
  const pct = progress.total > 0
    ? Math.min(100, Math.round((progress.done / progress.total) * 100))
    : (isBusy ? 4 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="image-list-pdf-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon size={16} /> Image List PDF — {moduleLabel || 'Closing Stock'}
          </DialogTitle>
          <DialogDescription>
            A4 portrait, 5 columns per row. Cards never split across pages.
            Generation runs in the background — feel free to keep working while it builds.
          </DialogDescription>
        </DialogHeader>

        {/* Branding card */}
        <Card className="p-3 bg-[var(--paper-warm)] border-[var(--divider)]">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {company?.has_logo && company?.logo_data_url ? (
                <img
                  src={company.logo_data_url}
                  alt=""
                  className="w-10 h-10 rounded object-contain bg-white border border-[var(--divider)]"
                />
              ) : (
                <div className="w-10 h-10 rounded bg-white border border-[var(--divider)] flex items-center justify-center text-slate-400">
                  <Building2 size={16} />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--ink-navy)] truncate">
                  {company?.name || 'Company'}
                </div>
                <div className="text-[11px] text-slate-500">
                  {company?.has_logo ? 'Logo & name available' : 'No logo uploaded — only the name can appear'}
                </div>
              </div>
            </div>
            <Badge className="text-[10px] bg-blue-50 text-blue-700">Branding</Badge>
          </div>
          <label
            className="flex items-start gap-3 mt-3 cursor-pointer select-none"
            data-testid="image-list-include-branding-label"
          >
            <Checkbox
              checked={includeBranding}
              onCheckedChange={(v) => setIncludeBranding(!!v)}
              data-testid="image-list-include-branding-checkbox"
              className="mt-0.5"
              disabled={isBusy}
            />
            <div>
              <div className="text-sm text-[var(--ink-navy)]">Include company logo / name on every page</div>
              <div className="text-[11px] text-slate-500">
                {includeBranding
                  ? (company?.has_logo
                      ? 'Logo and company name will appear in the page header.'
                      : 'Only the company name will appear (no logo has been uploaded).')
                  : 'The company logo and name will be hidden on this document.'}
              </div>
            </div>
          </label>
        </Card>

        {/* Grouping card */}
        <Card className="p-3 bg-white border-[var(--divider)] mt-3">
          <label
            className="flex items-start gap-3 cursor-pointer select-none"
            data-testid="image-list-grouped-label"
          >
            <Checkbox
              checked={grouped}
              onCheckedChange={(v) => setGrouped(!!v)}
              data-testid="image-list-grouped-checkbox"
              className="mt-0.5"
              disabled={isBusy}
            />
            <div className="flex-1">
              <div className="text-sm text-[var(--ink-navy)] flex items-center gap-1.5">
                <Layers size={13} /> Group by classification
              </div>
              <div className="text-[11px] text-slate-500">
                {grouped
                  ? 'Each classification gets its own banner, then its packet cards in 5-column rows.'
                  : 'Packets stream continuously in a single 5-column grid, with no classification banners.'}
              </div>
            </div>
          </label>
        </Card>

        {/* Progress */}
        {jobStatus && jobStatus !== 'failed' && (
          <div className="mt-3 space-y-1" data-testid="image-list-progress">
            <div className="flex items-center justify-between text-[11px] text-slate-600">
              <span>
                {jobStatus === 'queued' && 'Queued…'}
                {jobStatus === 'running' && 'Generating PDF…'}
                {jobStatus === 'ready' && (downloading ? 'Downloading…' : 'Ready')}
              </span>
              <span className="font-mono">
                {progress.total > 0
                  ? `${progress.done} / ${progress.total} packets`
                  : (jobStatus === 'queued' ? 'Preparing…' : '')}
              </span>
            </div>
            <Progress value={pct} className="h-1.5" />
          </div>
        )}
        {jobStatus === 'failed' && (
          <div className="mt-3 p-2 rounded-md bg-rose-50 border border-rose-200 text-[12px] text-rose-700">
            Generation failed. Please try again, or contact support if this keeps happening.
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isBusy}
            data-testid="image-list-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={startJob}
            className="luxury-button"
            disabled={isBusy}
            data-testid="image-list-generate-button"
          >
            {isBusy ? (
              <>
                <FileText size={14} className="mr-1.5 animate-pulse" />
                {jobStatus === 'ready' ? 'Downloading…' : 'Generating…'}
              </>
            ) : (
              <>
                <Download size={14} className="mr-1.5" /> Generate PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImageListPdfDialog;
